import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useArtifacts, useDeliverables } from "@/hooks/useLive";
import type { Artifact, ArtifactKind, Deliverable } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/Card";
import { CreateArtifactModal, EditArtifactModal } from "@/components/forms/CreateModals";
import { Avatar } from "@/components/ui/Avatar";
import { cn, primaryOwner } from "@/lib/utils";
import {
  ExternalLink,
  FileText,
  Film,
  Frame,
  Image as ImageIcon,
  Link2,
  Pencil,
  Plus,
  Presentation,
  Search,
  Sheet as SheetIcon,
  Trash2,
  Video,
} from "lucide-react";

const KIND_META: Record<ArtifactKind, { label: string; icon: typeof FileText; accent: string }> = {
  doc: { label: "Doc", icon: FileText, accent: "#3b82f6" },
  slide: { label: "Slides", icon: Presentation, accent: "#f59e0b" },
  sheet: { label: "Sheet", icon: SheetIcon, accent: "#10b981" },
  figma: { label: "Figma", icon: Frame, accent: "#a855f7" },
  loom: { label: "Loom", icon: Video, accent: "#ef4444" },
  video: { label: "Video", icon: Film, accent: "#ec4899" },
  image: { label: "Image", icon: ImageIcon, accent: "#14b8a6" },
  other: { label: "Link", icon: Link2, accent: "#6b7280" },
};

function kindOf(a: Artifact): ArtifactKind {
  if (a.kind) return a.kind;
  const url = (a.link ?? "").toLowerCase();
  if (url.includes("figma.com")) return "figma";
  if (url.includes("loom.com")) return "loom";
  if (url.includes("docs.google.com/presentation")) return "slide";
  if (url.includes("docs.google.com/spreadsheets")) return "sheet";
  if (url.includes("docs.google.com")) return "doc";
  if (/\.(png|jpe?g|gif|webp)$/.test(url)) return "image";
  if (/\.(mp4|mov|webm)$/.test(url)) return "video";
  return "other";
}

function ArtifactCard({
  artifact,
  tasksById,
  onEdit,
  onDelete,
}: {
  artifact: Artifact;
  tasksById: Map<string, Deliverable>;
  onEdit: (a: Artifact) => void;
  onDelete: (a: Artifact) => void;
}) {
  const kind = kindOf(artifact);
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const linkedTasks = (artifact.taskIds ?? []).map((id) => tasksById.get(String(id))).filter(Boolean) as Deliverable[];
  const missingCount = (artifact.taskIds ?? []).length - linkedTasks.length;

  return (
    <div
      className="group relative flex flex-col gap-3 overflow-hidden rounded-xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
      style={{ borderTop: `4px solid ${meta.accent}` }}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-micro font-medium"
          style={{ backgroundColor: `${meta.accent}1a`, color: meta.accent }}
        >
          <Icon size={11} /> {meta.label}
        </span>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(artifact); }}
            title="Edit asset"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(artifact); }}
            title="Delete asset"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 size={12} />
          </button>
          <a
            href={artifact.link}
            target="_blank"
            rel="noopener noreferrer"
            title="Open link"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-primary"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={12} />
          </a>
        </div>
      </div>
      <a
        href={artifact.link}
        target="_blank"
        rel="noopener noreferrer"
        className="text-body font-semibold leading-snug text-foreground line-clamp-3 hover:text-primary"
      >
        {artifact.name}
      </a>
      {linkedTasks.length === 0 && missingCount === 0 ? (
        <div className="rounded-md bg-muted/30 px-2 py-1.5 text-micro italic text-muted-foreground">
          Not linked to a task
        </div>
      ) : (
        <div className="rounded-md bg-muted/40 px-2 py-1.5 text-micro text-muted-foreground space-y-1">
          <div className="font-medium text-foreground/80">{linkedTasks.length === 1 ? "Linked task" : `Linked tasks (${linkedTasks.length})`}</div>
          {linkedTasks.map((t) => (
            <div key={t.id} className="line-clamp-1">· {t.name}</div>
          ))}
          {missingCount > 0 && (
            <div className="text-destructive">· {missingCount} task{missingCount > 1 ? "s" : ""} not found</div>
          )}
        </div>
      )}
      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar name={primaryOwner(artifact.owner ?? linkedTasks[0]?.owner ?? "")} size="xs" />
          <span className="truncate text-micro text-muted-foreground">
            {artifact.owner || linkedTasks[0]?.owner || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

export function DeliverablesCardPanel() {
  const qc = useQueryClient();
  const artifacts = useArtifacts();
  const tasks = useDeliverables();
  const tasksById = useMemo(() => {
    const m = new Map<string, Deliverable>();
    tasks.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [tasks]);

  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | ArtifactKind>("all");
  const [taskFilter, setTaskFilter] = useState<"all" | "unlinked" | string>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Artifact | null>(null);

  const filtered = useMemo(() => {
    return artifacts.filter((a) => {
      if (kindFilter !== "all" && kindOf(a) !== kindFilter) return false;
      const ids = a.taskIds ?? [];
      if (taskFilter === "unlinked") {
        if (ids.length > 0) return false;
      } else if (taskFilter !== "all") {
        if (!ids.map(String).includes(taskFilter)) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        const taskNames = ids.map((id) => tasksById.get(String(id))?.name ?? "").join(" ");
        const hay = `${a.name} ${a.owner ?? ""} ${a.notes ?? ""} ${taskNames}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [artifacts, kindFilter, taskFilter, query, tasksById]);

  const tasksWithArtifacts = useMemo(() => {
    const ids = new Set<string>();
    artifacts.forEach((a) => (a.taskIds ?? []).forEach((id) => ids.add(String(id))));
    return tasks.filter((t) => ids.has(String(t.id)));
  }, [artifacts, tasks]);

  const handleDelete = async (a: Artifact) => {
    if (!confirm(`Delete asset "${a.name}"?`)) return;
    try {
      const res = await fetch(`/api/artifacts/${a.id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      qc.invalidateQueries({ queryKey: ["artifacts"] });
    } catch (err) {
      alert(`Delete failed: ${err instanceof Error ? err.message : "unknown"}`);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <div className="relative min-w-0 flex-1">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search assets…"
              className="h-8 w-full rounded-md border border-input bg-card pl-8 pr-3 text-caption placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as typeof kindFilter)}
            className="h-8 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
          >
            <option value="all">All kinds</option>
            {(Object.keys(KIND_META) as ArtifactKind[]).map((k) => (
              <option key={k} value={k}>{KIND_META[k].label}</option>
            ))}
          </select>
          <select
            value={taskFilter}
            onChange={(e) => setTaskFilter(e.target.value)}
            className="h-8 max-w-[16rem] rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
          >
            <option value="all">All tasks</option>
            <option value="unlinked">— Unlinked only —</option>
            {tasksWithArtifacts.map((t) => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90"
          >
            <Plus size={13} /> Add
          </button>
        </CardContent>
      </Card>

      {artifacts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card py-16 text-center text-caption text-muted-foreground">
          <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <Link2 size={18} />
          </div>
          <p className="mb-1 text-body font-medium text-foreground">No assets yet</p>
          <p>Assets are the concrete, openable things — docs, decks, Looms, designs — that ship out of a task.</p>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className={cn("mt-4 inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90")}
          >
            <Plus size={13} /> Add the first asset
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card py-16 text-center text-caption text-muted-foreground">
          No assets match these filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((a) => (
            <ArtifactCard
              key={a.id}
              artifact={a}
              tasksById={tasksById}
              onEdit={setEditing}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <CreateArtifactModal open={addOpen} onClose={() => setAddOpen(false)} />
      <EditArtifactModal open={!!editing} onClose={() => setEditing(null)} artifact={editing} />
    </div>
  );
}
